import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Save, RotateCcw, Check, AlertCircle, ChevronDown } from 'lucide-react';
import { adminApi } from '@/lib/adminApi';
import { translations } from '@/lib/i18n';

/*
 * Form schema. Each entry maps to a path inside t[lang]. Keep this list in
 * sync with the fields actually rendered by the home page components so the
 * editor never collects strings nobody reads.
 */
const SECTIONS = [
  {
    key: 'hero',
    label: 'Hero',
    description: 'Opening block — large title, lead paragraph and the 4 benefit bullets.',
    fields: [
      { path: ['hero', 'titleA'], label: 'Title (line 1)', type: 'text' },
      { path: ['hero', 'titleB'], label: 'Title (line 2, highlighted)', type: 'text' },
      { path: ['hero', 'sub'], label: 'Subtitle paragraph (below bullets)', type: 'textarea' },
      { path: ['hero', 'stat1'], label: 'Benefit bullet 1' },
      { path: ['hero', 'stat2'], label: 'Benefit bullet 2' },
      { path: ['hero', 'stat3'], label: 'Benefit bullet 3' },
      { path: ['hero', 'stat4'], label: 'Benefit bullet 4' },
    ],
  },
  {
    key: 'how',
    label: 'How It Works',
    description: 'The 4 steps block below the hero. Each step has a short title and 1-line description.',
    fields: [
      { path: ['how', 'steps', 0, 't'], label: 'Step 1 · title' },
      { path: ['how', 'steps', 0, 'd'], label: 'Step 1 · description', type: 'textarea' },
      { path: ['how', 'steps', 1, 't'], label: 'Step 2 · title' },
      { path: ['how', 'steps', 1, 'd'], label: 'Step 2 · description', type: 'textarea' },
      { path: ['how', 'steps', 2, 't'], label: 'Step 3 · title' },
      { path: ['how', 'steps', 2, 'd'], label: 'Step 3 · description', type: 'textarea' },
      { path: ['how', 'steps', 3, 't'], label: 'Step 4 · title' },
      { path: ['how', 'steps', 3, 'd'], label: 'Step 4 · description', type: 'textarea' },
    ],
  },
  {
    key: 'feeder',
    label: 'SmartPaw Feeder spotlight',
    description: 'The product-highlight block with the feeder photo and 4 feature bullets.',
    fields: [
      { path: ['feeder', 'eyebrow'], label: 'Eyebrow tag (small label above title)' },
      { path: ['feeder', 'title'], label: 'Section title' },
      { path: ['feeder', 'lead'], label: 'Lead paragraph', type: 'textarea' },
      { path: ['feeder', 'bullet1'], label: 'Bullet 1' },
      { path: ['feeder', 'bullet2'], label: 'Bullet 2' },
      { path: ['feeder', 'bullet3'], label: 'Bullet 3' },
      { path: ['feeder', 'bullet4'], label: 'Bullet 4' },
    ],
  },
  {
    key: 'experts',
    label: 'Our Experts',
    description: 'Giorgi\u2019s portrait block and consultation CTA.',
    fields: [
      { path: ['experts', 'eyebrow'], label: 'Eyebrow tag' },
      { path: ['experts', 'title'], label: 'Section title' },
      { path: ['experts', 'name'], label: 'Expert name (shown on photo)' },
      { path: ['experts', 'role'], label: 'Expert role / credentials' },
      { path: ['experts', 'bio'], label: 'Bio paragraph', type: 'textarea' },
      { path: ['experts', 'benefitsTitle'], label: 'Benefits sub-heading' },
      { path: ['experts', 'bullet1'], label: 'Bullet 1' },
      { path: ['experts', 'bullet2'], label: 'Bullet 2' },
      { path: ['experts', 'bullet3'], label: 'Bullet 3' },
      { path: ['experts', 'bullet4'], label: 'Bullet 4' },
      { path: ['experts', 'cta'], label: 'CTA button label' },
    ],
  },
  {
    key: 'testimonials',
    label: 'Testimonials strip (header only)',
    description: 'Eyebrow, title and subtitle shown above the four testimonial cards. Manage individual cards in the Testimonials page.',
    fields: [
      { path: ['testimonials', 'eyebrow'], label: 'Eyebrow tag' },
      { path: ['testimonials', 'title'], label: 'Section title' },
      { path: ['testimonials', 'subtitle'], label: 'Subtitle paragraph', type: 'textarea' },
    ],
  },
  {
    key: 'contactSection',
    label: 'Contact block',
    description: 'The final block before the footer.',
    fields: [
      { path: ['contactSection', 'eyebrow'], label: 'Eyebrow tag' },
      { path: ['contactSection', 'title'], label: 'Section title' },
      { path: ['contactSection', 'subtitle'], label: 'Subtitle paragraph', type: 'textarea' },
    ],
  },
];

/**
 * Read a dotted/indexed path out of a nested object. Missing branches return
 * empty string so the UI never crashes on partial dictionaries.
 */
function readPath(root, path) {
  let cur = root;
  for (const seg of path) {
    if (cur == null) return '';
    cur = cur[seg];
  }
  return cur == null ? '' : cur;
}

/**
 * Immutable deep-set. Mirrors `readPath` but writes a new tree.
 */
function setPath(root, path, value) {
  if (path.length === 0) return value;
  const [head, ...rest] = path;
  const isArrayIndex = typeof head === 'number';
  const container = root == null
    ? (isArrayIndex ? [] : {})
    : (isArrayIndex && !Array.isArray(root) ? [] : (Array.isArray(root) ? [...root] : { ...root }));
  container[head] = setPath(container[head], rest, value);
  return container;
}

export default function AdminHomepageContent() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successAt, setSuccessAt] = useState(0);
  // Shape: { en: {hero:{...}}, ka: {...} }
  const [draft, setDraft] = useState({ en: {}, ka: {} });
  const [initial, setInitial] = useState({ en: {}, ka: {} });
  const [expanded, setExpanded] = useState(() => new Set(['hero']));

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminApi.getHomepageContent();
      const sections = res?.sections || { en: {}, ka: {} };
      setDraft(sections);
      setInitial(JSON.parse(JSON.stringify(sections)));
    } catch (e) {
      setError(e.message || 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const isDirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(initial), [draft, initial]);

  const update = (lang, path, value) => {
    setDraft((prev) => {
      const next = { ...prev };
      next[lang] = setPath(prev[lang] || {}, path, value);
      return next;
    });
    setSuccessAt(0);
  };

  const resetField = (lang, path) => {
    setDraft((prev) => {
      const next = { ...prev };
      next[lang] = setPath(prev[lang] || {}, path, '');
      return next;
    });
  };

  const toggleSection = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await adminApi.saveHomepageContent(draft);
      const sections = res?.sections || draft;
      setDraft(sections);
      setInitial(JSON.parse(JSON.stringify(sections)));
      setSuccessAt(Date.now());
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const revert = () => {
    setDraft(JSON.parse(JSON.stringify(initial)));
    setSuccessAt(0);
  };

  return (
    <div className="space-y-6" data-testid="admin-homepage-content">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#F25C05] font-bold">Content</p>
          <h1 className="font-display font-extrabold text-2xl md:text-3xl text-[#05223D] mt-1">
            Homepage text editor
          </h1>
          <p className="mt-2 text-sm text-[#465B70] max-w-2xl">
            Change any copy on the public landing page. Leave a field blank to keep the shipped default
            (shown as placeholder). Saves apply instantly to the live site.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={revert}
            disabled={!isDirty || saving}
            data-testid="admin-homepage-revert"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0A4D8C]/30 text-[#0A4D8C] text-sm font-semibold hover:bg-[#0A4D8C]/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <RotateCcw size={14} /> Revert
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!isDirty || saving}
            data-testid="admin-homepage-save"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#F25C05] text-white text-sm font-bold shadow-[0_6px_20px_rgba(242,92,5,0.25)] hover:bg-[#D44D04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </header>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-900" role="alert">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {successAt > 0 && !isDirty && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-sm text-emerald-900" role="status">
          <Check size={16} /> Saved. The public site is already showing the new copy.
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-[#465B70]">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : (
        <div className="space-y-4">
          {SECTIONS.map((section) => (
            <SectionEditor
              key={section.key}
              section={section}
              draft={draft}
              onChange={update}
              onReset={resetField}
              expanded={expanded.has(section.key)}
              onToggle={() => toggleSection(section.key)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Section editor                                                             */
/* -------------------------------------------------------------------------- */

function SectionEditor({ section, draft, onChange, onReset, expanded, onToggle }) {
  return (
    <section
      className="bg-white border border-[#eadfce] rounded-2xl overflow-hidden"
      data-testid={`admin-homepage-section-${section.key}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 px-5 md:px-6 py-4 text-left hover:bg-[#FDFBF7]/60 transition-colors"
        aria-expanded={expanded}
      >
        <div className="min-w-0">
          <p className="font-display font-bold text-[#05223D] text-base md:text-lg leading-tight">
            {section.label}
          </p>
          <p className="text-[12px] text-[#465B70] mt-1 leading-relaxed">
            {section.description}
          </p>
        </div>
        <ChevronDown
          size={18}
          className={`shrink-0 text-[#0A4D8C] transition-transform ${expanded ? 'rotate-180' : ''}`}
        />
      </button>

      {expanded && (
        <div className="px-5 md:px-6 pb-6 pt-1 space-y-5">
          {section.fields.map((field, i) => (
            <FieldRow
              key={i}
              field={field}
              draft={draft}
              onChange={onChange}
              onReset={onReset}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Field row — EN / KA side-by-side with placeholder from shipped i18n        */
/* -------------------------------------------------------------------------- */

function FieldRow({ field, draft, onChange, onReset }) {
  const isTextarea = field.type === 'textarea';
  const enDefault = readPath(translations.en, field.path);
  const kaDefault = readPath(translations.ka, field.path);
  const enValue = readPath(draft.en || {}, field.path);
  const kaValue = readPath(draft.ka || {}, field.path);
  const Comp = isTextarea ? 'textarea' : 'input';
  const common = {
    className:
      'w-full rounded-xl border border-[#eadfce] bg-[#FDFBF7] px-3 py-2 text-sm text-[#05223D] placeholder:text-[#9CA7B4] focus:outline-none focus:ring-2 focus:ring-[#F25C05]/40 focus:border-[#F25C05] transition-colors',
    ...(isTextarea ? { rows: 3 } : { type: 'text' }),
  };

  const path = field.path;
  const testKey = path.filter((p) => typeof p === 'string' || typeof p === 'number').join('-');

  return (
    <div>
      <div className="flex items-end justify-between gap-2 mb-1.5">
        <p className="font-semibold text-[13px] text-[#05223D]">{field.label}</p>
        {(enValue || kaValue) && (
          <button
            type="button"
            onClick={() => { onReset('en', path); onReset('ka', path); }}
            className="text-[11px] text-[#9CA7B4] hover:text-[#F25C05] font-semibold"
            data-testid={`admin-homepage-reset-${testKey}`}
          >
            Clear override
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-[10.5px] font-bold uppercase tracking-[0.14em] text-[#0A4D8C] mb-1">
            English
          </label>
          <Comp
            {...common}
            value={enValue}
            placeholder={enDefault || '—'}
            onChange={(e) => onChange('en', path, e.target.value)}
            data-testid={`admin-homepage-en-${testKey}`}
          />
        </div>
        <div>
          <label className="block text-[10.5px] font-bold uppercase tracking-[0.14em] text-[#F25C05] mb-1">
            ქართული
          </label>
          <Comp
            {...common}
            value={kaValue}
            placeholder={kaDefault || '—'}
            onChange={(e) => onChange('ka', path, e.target.value)}
            data-testid={`admin-homepage-ka-${testKey}`}
          />
        </div>
      </div>
    </div>
  );
}
